/**
 * @fileoverview Error formatting utilities
 *
 * Purpose: Extract clean, user-facing messages from Convex/JS errors so toasts
 * and inline error UI never expose internal noise like
 * `[CONVEX M(...)] [Request ID: ...] Server Error Uncaught Error:` or stack
 * traces.
 */

/**
 * Extract a clean, user-facing message from a Convex (or generic) error.
 *
 * Convex server errors arrive on the client wrapped with extra metadata, e.g.:
 *
 *   [CONVEX M(foo:bar)] [Request ID: abc] Server Error
 *   Uncaught Error: <the actual message>
 *       at handler (../../../convex/foo.ts:10:8)
 *     Called by client
 *
 * This helper strips the wrapper and returns just the message thrown by the
 * mutation/query/action. Falls back to the supplied default when no Error is
 * provided or no message can be extracted.
 *
 * @example
 *   try { await mutate(...); }
 *   catch (err) { toast.error(extractErrorMessage(err, "Failed to save")); }
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) {
    return typeof err === "string" && err.trim() ? err : fallback;
  }

  // Strip Convex client-side wrapper prefixes.
  const unwrapped = err.message
    .replace(/^\[CONVEX[^\]]*\]\s*/, "")
    .replace(/^\[Request ID:[^\]]*\]\s*/, "")
    .replace(/^Server Error\s*/, "")
    .replace(/^Uncaught Error:\s*/, "")
    .replace(/^Error:\s*/, "");

  // Cut everything from the first stack frame (e.g. "  at handler (...)").
  const stackIdx = unwrapped.search(/\s+at\s+\S+\s*\(/);
  const withoutStack = stackIdx >= 0 ? unwrapped.slice(0, stackIdx) : unwrapped;

  // Strip trailing Convex marker.
  const withoutMarker = withoutStack.replace(/\s*Called by client\s*$/, "");

  // Limit to the first line in case anything else slipped through.
  const cleaned = withoutMarker.split("\n")[0]?.trim() ?? withoutMarker.trim();

  return cleaned || fallback;
}
